import React from 'react';
import { Box, Grid, Paper, Typography, Select, MenuItem, FormControl, InputLabel, Stack } from '@mui/material';
import { getLayoutDef } from '../layouts';
import DashboardWidgetContent from './DashboardWidgetContent';
import { DashboardWidgetData } from './CustomChartWidget';
import { DashboardData } from '../hooks/useDashboards';

interface DashboardGridProps {
  dashboard: DashboardData | null;
  widgets: DashboardWidgetData[];
  editMode: boolean;
  daysCount: number;
  onSetSlot: (slotIndex: number, widgetId: string | null) => void;
}

const EmptySlot: React.FC<{ minHeight: number }> = ({ minHeight }) => (
  <Paper
    variant="outlined"
    sx={{
      minHeight,
      height: '100%',
      borderRadius: 2,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderStyle: 'dashed',
      backgroundColor: 'background.paper',
    }}
  >
    <Typography variant="body2" color="text.secondary">
      Assign a graph
    </Typography>
  </Paper>
);

const DashboardGrid: React.FC<DashboardGridProps> = ({
  dashboard,
  widgets,
  editMode,
  daysCount,
  onSetSlot,
}) => {
  if (!dashboard) {
    return (
      <Paper variant="outlined" sx={{ p: 4, borderRadius: 2, textAlign: 'center' }}>
        <Typography color="text.secondary">No dashboards yet. Create one to get started.</Typography>
      </Paper>
    );
  }

  const layout = getLayoutDef(dashboard.layoutKey);

  return (
    <Grid container spacing={3}>
      {layout.slots.map((slotDef, index) => {
        const widget = dashboard.slots?.[index] ?? null;
        return (
          <Grid item xs={12} md={slotDef.colSpan} key={`${dashboard.id}-slot-${index}`}>
            <Box sx={{ minHeight: slotDef.minHeight, display: 'flex', flexDirection: 'column' }}>
              {editMode && (
                <FormControl size="small" fullWidth sx={{ mb: 1 }}>
                  <InputLabel>Slot {index + 1}</InputLabel>
                  <Select
                    label={`Slot ${index + 1}`}
                    value={widget?.id ?? ''}
                    onChange={(e) => onSetSlot(index, e.target.value ? (e.target.value as string) : null)}
                  >
                    <MenuItem value="">— Empty —</MenuItem>
                    {widgets.map((w) => (
                      <MenuItem key={w.id} value={w.id}>
                        {w.title}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {widget ? (
                <Box sx={{ flexGrow: 1, minHeight: 0 }}>
                  <DashboardWidgetContent widget={widget} daysCount={daysCount} />
                </Box>
              ) : (
                <Box sx={{ flexGrow: 1, minHeight: 0 }}>
                  <EmptySlot minHeight={slotDef.minHeight} />
                </Box>
              )}
            </Box>
          </Grid>
        );
      })}
    </Grid>
  );
};

export default DashboardGrid;
